import { Card, CardHeader, Button, Badge } from '@repo/ui-core'
import { useEffect, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { journalEntryRepo } from '../../lib/db'
import type { JournalEntry } from '@repo/entity-accounting'
import { Plus, Search, BookOpen, CheckCircle, Clock, Eye, X, Filter } from 'lucide-react'

export function JournalEntryListPage() {
  const [entries, setEntries] = useState<JournalEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterType, setFilterType] = useState<string>('all')
  const [selectedEntry, setSelectedEntry] = useState<JournalEntry | null>(null)

  const loadEntries = async () => {
    setLoading(true)
    try {
      const result = await journalEntryRepo.findMany({ page: 1, pageSize: 100 })
      if ('items' in result) {
        const sorted = (result.items as JournalEntry[]).sort((a, b) => b.entryDate - a.entryDate)
        setEntries(sorted)
      }
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadEntries()
  }, [])

  const filteredEntries = entries.filter(entry => {
    const matchesSearch =
      (entry.referenceNumber && entry.referenceNumber.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (entry.description && entry.description.toLowerCase().includes(searchTerm.toLowerCase()))
    const matchesType = filterType === 'all' || entry.entryType === filterType
    return matchesSearch && matchesType
  })

  const totalDebits = entries.reduce((s, e) => s + (e.totalDebit || 0), 0)
  const totalCredits = entries.reduce((s, e) => s + (e.totalCredit || 0), 0)

  return (
    <div className="p-6 space-y-6">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Journal Entries</h1>
          <p className="text-sm text-gray-500">General Ledger transaction journal and double-entry postings</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/accounting/journal-entries/new">
            <Button icon={<Plus className="h-4 w-4" />}>New Journal Entry</Button>
          </Link>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Total Journal Entries</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{entries.length}</p>
          </div>
          <div className="p-3 bg-blue-50 text-blue-600 rounded-lg">
            <BookOpen className="h-6 w-6" />
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Total Posted Debits</p>
            <p className="text-2xl font-bold text-emerald-600 mt-1">₱{totalDebits.toLocaleString('en-PH', { minimumFractionDigits: 2 })}</p>
          </div>
          <div className="p-3 bg-emerald-50 text-emerald-600 rounded-lg">
            <CheckCircle className="h-6 w-6" />
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Total Posted Credits</p>
            <p className="text-2xl font-bold text-emerald-600 mt-1">₱{totalCredits.toLocaleString('en-PH', { minimumFractionDigits: 2 })}</p>
          </div>
          <div className="p-3 bg-emerald-50 text-emerald-600 rounded-lg">
            <CheckCircle className="h-6 w-6" />
          </div>
        </div>
      </div>

      {/* Main Table Card */}
      <Card>
        <div className="p-4 border-b border-gray-200 flex flex-col md:flex-row gap-4 justify-between items-center bg-gray-50/50">
          <div className="relative w-full md:w-80">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by ref # or memo..."
              className="w-full pl-9 pr-3 py-1.5 text-sm bg-white border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="flex items-center gap-1.5 overflow-x-auto w-full md:w-auto">
            {['all', 'manual', 'auto', 'adjusting', 'closing'].map(type => (
              <button
                key={type}
                onClick={() => setFilterType(type)}
                className={`px-3 py-1 text-xs font-medium rounded-full capitalize transition-colors ${
                  filterType === type
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {type === 'all' ? 'All Types' : type}
              </button>
            ))}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Reference #</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Description / Memo</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Type</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">Debit (₱)</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">Credit (₱)</th>
                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-500">Status</th>
                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-500">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {filteredEntries.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-gray-400">
                    <p className="text-base font-medium text-gray-600 mb-1">No Journal Entries Found</p>
                    <p className="text-xs text-gray-400">Create a new entry or import transactions to populate the general ledger.</p>
                  </td>
                </tr>
              ) : (
                filteredEntries.map(entry => (
                  <tr key={entry.id} className="hover:bg-gray-50">
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600 font-mono">
                      {new Date(entry.entryDate).toLocaleDateString()}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm font-semibold text-blue-600 font-mono">
                      {entry.referenceNumber || `JV-${entry.id.slice(-6).toUpperCase()}`}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 max-w-xs truncate">
                      {entry.description}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3">
                      <Badge color="gray" size="sm">
                        {entry.entryType}
                      </Badge>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-right font-mono font-medium text-gray-900">
                      ₱{(entry.totalDebit || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-right font-mono font-medium text-gray-900">
                      ₱{(entry.totalCredit || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-center">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-800">
                        Posted
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-center">
                      <button
                        onClick={() => setSelectedEntry(entry)}
                        className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800"
                      >
                        <Eye className="h-3.5 w-3.5" /> Details
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Entry Detail Drawer / Modal */}
      {selectedEntry && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full p-6 space-y-4">
            <div className="flex justify-between items-center pb-3 border-b border-gray-100">
              <div>
                <h2 className="text-lg font-bold text-gray-900">
                  Journal Entry: {selectedEntry.referenceNumber || `JV-${selectedEntry.id.slice(-6).toUpperCase()}`}
                </h2>
                <p className="text-xs text-gray-500">
                  Date: {new Date(selectedEntry.entryDate).toLocaleDateString()} | Type: {selectedEntry.entryType}
                </p>
              </div>
              <button
                onClick={() => setSelectedEntry(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="bg-gray-50 p-3 rounded-lg text-sm text-gray-700">
              <span className="font-semibold text-gray-900">Memo: </span>
              {selectedEntry.description}
            </div>

            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Account</th>
                    <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500">Debit (₱)</th>
                    <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500">Credit (₱)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {((selectedEntry as any).lines || []).length > 0 ? (
                    ((selectedEntry as any).lines || []).map((line: any, idx: number) => (
                      <tr key={idx}>
                        <td className="px-3 py-2 text-gray-900 font-mono">
                          {line.accountCode} {line.accountName ? `- ${line.accountName}` : ''}
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-gray-900">
                          {line.debitAmount ? `₱${Number(line.debitAmount).toFixed(2)}` : '—'}
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-gray-900">
                          {line.creditAmount ? `₱${Number(line.creditAmount).toFixed(2)}` : '—'}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td className="px-3 py-2 text-gray-900 font-mono">General Journal Summary</td>
                      <td className="px-3 py-2 text-right font-mono text-gray-900">
                        ₱{Number(selectedEntry.totalDebit || 0).toFixed(2)}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-gray-900">
                        ₱{Number(selectedEntry.totalCredit || 0).toFixed(2)}
                      </td>
                    </tr>
                  )}
                </tbody>
                <tfoot className="bg-gray-50 font-bold">
                  <tr>
                    <td className="px-3 py-2 text-gray-900">Total</td>
                    <td className="px-3 py-2 text-right text-emerald-700 font-mono">
                      ₱{Number(selectedEntry.totalDebit || 0).toFixed(2)}
                    </td>
                    <td className="px-3 py-2 text-right text-emerald-700 font-mono">
                      ₱{Number(selectedEntry.totalCredit || 0).toFixed(2)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <div className="flex justify-end pt-2">
              <Button onClick={() => setSelectedEntry(null)}>Close</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
